/**
 * Crann v2 Store
 *
 * The central state hub that runs in the service worker.
 * This is NOT a singleton - each call to createStore() returns a new instance.
 */

import { source } from "../transport";
import type { Agent, AgentInfo, BrowserLocation } from "../transport";
import {
  ConfigSchema,
  ValidatedConfig,
  StoreOptions,
  DerivedState,
  DerivedSharedState,
  DerivedAgentState,
  StateChanges,
  StateChangeListener,
  StateSubscriber,
  AgentConnectionInfo,
  isStateItem,
  Scope,
} from "./types";
import { StateManager } from "./StateManager";
import { Persistence } from "./Persistence";
import { AgentRegistry } from "./AgentRegistry";
import { ActionExecutor } from "./ActionExecutor";
import { LifecycleError } from "../errors";

// =============================================================================
// Store Class
// =============================================================================

export class Store<TConfig extends ConfigSchema> {
  private readonly config: ValidatedConfig<TConfig>;
  private readonly options: StoreOptions;
  private readonly porter: ReturnType<typeof source>;

  private readonly stateManager: StateManager<TConfig>;
  private readonly persistence: Persistence<TConfig>;
  private readonly agentRegistry: AgentRegistry;
  private readonly actionExecutor: ActionExecutor<TConfig>;

  private readonly stateChangeListeners: Set<StateSubscriber<TConfig>> = new Set();
  private readonly agentConnectListeners: Set<(agent: AgentConnectionInfo) => void> = new Set();
  private readonly agentDisconnectListeners: Set<(agent: AgentConnectionInfo) => void> = new Set();

  private isDestroyed = false;

  constructor(config: ValidatedConfig<TConfig>, options: StoreOptions = {}) {
    this.config = config;
    this.options = options;

    // Initialize porter with the store name as namespace
    this.porter = source(config.name, { debug: options.debug ?? false });

    // Initialize sub-modules
    this.persistence = new Persistence(config, options);
    this.stateManager = new StateManager(config, this.persistence);
    this.agentRegistry = new AgentRegistry();
    this.actionExecutor = new ActionExecutor(
      config,
      () => this.getState(),
      (state, agentId) => this.setState(state, agentId)
    );

    // Set up message handlers
    this.setupMessageHandlers();

    // Hydrate state from storage
    this.hydrate();
  }

  // ===========================================================================
  // Public API - State
  // ===========================================================================

  /**
   * Get the current shared state (state visible to all agents).
   *
   * @returns A snapshot of the current shared state
   * @throws {LifecycleError} If the store has been destroyed
   *
   * @example
   * const state = store.getState();
   * console.log(state.count);
   */
  getState(): DerivedState<TConfig> {
    this.assertNotDestroyed();
    return this.stateManager.getState();
  }

  /**
   * Get agent-scoped state for a specific agent.
   *
   * @param agentId - The unique identifier of the agent
   * @returns The agent-scoped state for the specified agent
   * @throws {LifecycleError} If the store has been destroyed
   *
   * @example
   * store.onAgentConnect((agent) => {
   *   const agentState = store.getAgentState(agent.id);
   * });
   */
  getAgentState(agentId: string): DerivedAgentState<TConfig> {
    this.assertNotDestroyed();
    return this.stateManager.getAgentState(agentId);
  }

  /**
   * Update shared state. Changes are persisted and broadcast to all agents.
   *
   * @param state - Partial state to merge with current shared state
   * @throws {LifecycleError} If the store has been destroyed
   *
   * @example
   * await store.setState({ count: 5 });
   */
  async setState(state: Partial<DerivedSharedState<TConfig>>): Promise<void>;
  /**
   * Update state for a specific agent. Can include both shared and agent-scoped state.
   *
   * @param state - Partial state to merge
   * @param agentId - The agent to update (for agent-scoped state)
   * @throws {LifecycleError} If the store has been destroyed
   *
   * @example
   * await store.setState({ count: 5, agentData: 'custom' }, agentId);
   */
  async setState(
    state: Partial<DerivedState<TConfig>>,
    agentId: string
  ): Promise<void>;
  async setState(
    state: Partial<DerivedState<TConfig>>,
    agentId?: string
  ): Promise<void> {
    this.assertNotDestroyed();

    const { sharedChanges, agentChanges } = this.stateManager.setState(state, agentId);

    // Persist shared state changes
    if (Object.keys(sharedChanges).length > 0) {
      await this.persistence.persist(sharedChanges);
    }

    // Notify listeners and agents
    this.notifyStateChange(state as StateChanges<TConfig>, agentId);
  }

  /**
   * Update agent-scoped state for a specific agent.
   *
   * @param agentId - The unique identifier of the agent
   * @param state - Partial agent-scoped state to merge
   * @throws {LifecycleError} If the store has been destroyed
   *
   * @example
   * await store.setAgentState(agentId, { agentData: 'custom' });
   */
  async setAgentState(
    agentId: string,
    state: Partial<DerivedAgentState<TConfig>>
  ): Promise<void> {
    this.assertNotDestroyed();
    this.stateManager.setAgentState(agentId, state);
    this.notifyStateChange(state as StateChanges<TConfig>, agentId);
  }

  /**
   * Clear all state back to defaults and remove persisted data.
   *
   * @throws {LifecycleError} If the store has been destroyed
   *
   * @example
   * await store.clear();
   */
  async clear(): Promise<void> {
    this.assertNotDestroyed();
    this.stateManager.clear();
    await this.persistence.clearAll();
    this.notifyStateChange({} as StateChanges<TConfig>);
  }

  // ===========================================================================
  // Public API - Subscriptions
  // ===========================================================================

  /**
   * Subscribe to state changes. Called whenever state is updated.
   *
   * Can subscribe to all changes or filter by specific keys.
   *
   * @param callbackOrKeys - Either a callback for all changes, or an array of keys to filter by
   * @param maybeCallback - Callback when first arg is keys array
   * @returns Unsubscribe function
   * @throws {LifecycleError} If the store has been destroyed
   *
   * @example
   * // Subscribe to all changes
   * const unsubscribe = store.subscribe((state, changes, agent) => {
   *   console.log('State changed:', changes);
   * });
   *
   * // Subscribe to specific keys only
   * const unsubscribe = store.subscribe(['active', 'initialized'], (state, changes, agent) => {
   *   // Only called when 'active' or 'initialized' change
   *   console.log('Relevant state changed:', changes);
   * });
   */
  subscribe(
    callbackOrKeys: StateChangeListener<TConfig> | Array<keyof DerivedState<TConfig>>,
    maybeCallback?: StateChangeListener<TConfig>
  ): () => void {
    this.assertNotDestroyed();

    let keys: Array<keyof DerivedState<TConfig>> | undefined;
    let callback: StateChangeListener<TConfig>;

    if (typeof callbackOrKeys === "function") {
      callback = callbackOrKeys;
    } else {
      keys = callbackOrKeys;
      callback = maybeCallback!;
    }

    const subscriber: StateSubscriber<TConfig> = { keys, callback };
    this.stateChangeListeners.add(subscriber);

    return () => {
      this.stateChangeListeners.delete(subscriber);
    };
  }

  /**
   * Register callback for when an agent connects.
   *
   * @param callback - Called with agent connection info
   * @returns Unsubscribe function
   * @throws {LifecycleError} If the store has been destroyed
   *
   * @example
   * store.onAgentConnect((agent) => {
   *   console.log(`Agent ${agent.id} connected from tab ${agent.tabId}`);
   * });
   */
  onAgentConnect(callback: (agent: AgentConnectionInfo) => void): () => void {
    this.assertNotDestroyed();
    this.agentConnectListeners.add(callback);
    return () => {
      this.agentConnectListeners.delete(callback);
    };
  }

  /**
   * Register callback for when an agent disconnects.
   *
   * @param callback - Called with agent connection info
   * @returns Unsubscribe function
   * @throws {LifecycleError} If the store has been destroyed
   *
   * @example
   * store.onAgentDisconnect((agent) => {
   *   console.log(`Agent ${agent.id} disconnected`);
   * });
   */
  onAgentDisconnect(callback: (agent: AgentConnectionInfo) => void): () => void {
    this.assertNotDestroyed();
    this.agentDisconnectListeners.add(callback);
    return () => {
      this.agentDisconnectListeners.delete(callback);
    };
  }

  // ===========================================================================
  // Public API - Agent Queries
  // ===========================================================================

  /**
   * Get all connected agents, optionally filtered by location.
   *
   * @param query - Optional filter by context, tabId, frameId
   * @returns Array of connected agent info
   * @throws {LifecycleError} If the store has been destroyed
   *
   * @example
   * const allAgents = store.getAgents();
   * const contentScripts = store.getAgents({ context: 'contentscript' });
   * const tab42Agents = store.getAgents({ tabId: 42 });
   */
  getAgents(query?: Partial<BrowserLocation>): AgentConnectionInfo[] {
    this.assertNotDestroyed();
    if (query) {
      const porterAgents = this.porter.queryAgents(query);
      return porterAgents
        .map((a) => this.agentRegistry.get(a.info.id))
        .filter((a): a is AgentConnectionInfo => a !== undefined);
    }
    return this.agentRegistry.getAll();
  }

  // ===========================================================================
  // Public API - Lifecycle
  // ===========================================================================

  /**
   * Destroy the store and clean up all resources.
   * After calling this, the store cannot be used. Use for testing or HMR cleanup.
   *
   * @param options.clearPersisted - If true, also clears persisted storage data
   *
   * @example
   * // In tests
   * afterEach(() => {
   *   store.destroy({ clearPersisted: true });
   * });
   */
  destroy(options?: { clearPersisted?: boolean }): void {
    if (this.isDestroyed) return;

    this.isDestroyed = true;

    // Clear all listeners
    this.stateChangeListeners.clear();
    this.agentConnectListeners.clear();
    this.agentDisconnectListeners.clear();

    // Clear agent registry
    this.agentRegistry.clear();

    // Optionally clear persisted data
    if (options?.clearPersisted) {
      this.persistence.clearAll();
    }

    // Note: Porter doesn't have a disconnect method, but we've cleaned up our state
  }

  // ===========================================================================
  // Private - Message Handling
  // ===========================================================================

  private setupMessageHandlers(): void {
    // Track which agents have received initial state
    const initializedAgents = new Set<string>();

    // Handle state updates from agents
    this.porter.on({
      setState: (message, agentInfo) => {
        if (!agentInfo) return;
        this.setState(message.payload.state, agentInfo.id);
      },
    });

    // Handle agent ready signal (messages established)
    this.porter.onMessagesSet((agentInfo: AgentInfo) => {
      if (!agentInfo || initializedAgents.has(agentInfo.id)) return;

      initializedAgents.add(agentInfo.id);

      // Send initial state to the agent
      const fullState = this.stateManager.getFullStateForAgent(agentInfo.id);
      this.porter.post(
        {
          action: "initialState",
          payload: { state: fullState, info: agentInfo },
        },
        agentInfo.location
      );
    });

    // Handle agent connections
    this.porter.onConnect((agentInfo: AgentInfo) => {
      if (!agentInfo) return;

      const connectionInfo = this.agentRegistry.add(agentInfo);
      this.stateManager.initializeAgentState(agentInfo.id);

      // Notify listeners
      this.agentConnectListeners.forEach((cb) => cb(connectionInfo));
    });

    // Handle agent disconnections
    this.porter.onDisconnect((agentInfo: AgentInfo) => {
      if (!agentInfo) return;

      const connectionInfo = this.agentRegistry.get(agentInfo.id);
      if (connectionInfo) {
        this.agentRegistry.remove(agentInfo.id);
        this.stateManager.removeAgentState(agentInfo.id);
        initializedAgents.delete(agentInfo.id);

        // Notify listeners
        this.agentDisconnectListeners.forEach((cb) => cb(connectionInfo));
      }
    });

    // Set up RPC for actions
    this.setupRpcHandlers();
  }

  private setupRpcHandlers(): void {
    this.porter.on({
      rpc: async (message, agentInfo) => {
        if (!agentInfo) return;

        const { callId, actionName, args } = message.payload;
        try {
          const result = await this.actionExecutor.execute(
            actionName,
            args,
            agentInfo
          );
          this.porter.post(
            {
              action: "rpcResult",
              payload: { callId, actionName, result, success: true },
            },
            agentInfo.location
          );
        } catch (error) {
          this.porter.post(
            {
              action: "rpcResult",
              payload: {
                callId,
                actionName,
                error: error instanceof Error ? error.message : String(error),
                success: false,
              },
            },
            agentInfo.location
          );
        }
      },
    });
  }

  // ===========================================================================
  // Private - State Hydration & Notification
  // ===========================================================================

  private async hydrate(): Promise<void> {
    const hydratedState = await this.persistence.hydrate();
    if (Object.keys(hydratedState).length > 0) {
      this.stateManager.hydrateSharedState(hydratedState);
    }
  }

  private notifyStateChange(changes: StateChanges<TConfig>, agentId?: string): void {
    const state = this.getState();
    const agentInfo = agentId
      ? this.porter.getAgentById(agentId)?.info
      : undefined;

    // Notify local listeners (filtered by keys if specified)
    this.stateChangeListeners.forEach((subscriber) => {
      // If subscriber has specific keys, check if any changed
      if (subscriber.keys) {
        const hasRelevantChange = subscriber.keys.some(
          (key) => key in changes
        );
        if (!hasRelevantChange) return;
      }

      try {
        subscriber.callback(state, changes, agentInfo);
      } catch (error) {
        console.error("[Crann Store] Error in subscriber callback:", error);
      }
    });

    // Notify connected agents
    if (agentId) {
      // Notify specific agent
      const agent = this.porter.getAgentById(agentId);
      if (agent?.info.location) {
        this.porter.post(
          { action: "stateUpdate", payload: { state: changes } },
          agent.info.location
        );
      }
    } else {
      // Notify all agents
      for (const agent of this.agentRegistry.getAll()) {
        this.porter.post(
          { action: "stateUpdate", payload: { state: changes } },
          agent.id
        );
      }
    }
  }

  // ===========================================================================
  // Private - Utilities
  // ===========================================================================

  private assertNotDestroyed(): void {
    if (this.isDestroyed) {
      throw new LifecycleError(this.config.name, "store");
    }
  }
}

// =============================================================================
// Factory Function
// =============================================================================

/**
 * Create a new Crann store instance.
 *
 * This should be called in your service worker/background script.
 * Each call creates a new, independent store instance (no singleton).
 *
 * @param config - Validated config from createConfig()
 * @param options - Optional store options
 * @param options.debug - Enable debug logging
 * @param options.migrate - Migration function for schema version changes
 * @returns A new Store instance
 *
 * @example
 * // background.ts
 * import { createConfig, createStore } from 'crann';
 *
 * const config = createConfig({
 *   name: 'myFeature',
 *   count: { default: 0, persist: 'local' },
 * });
 *
 * const store = createStore(config, { debug: true });
 *
 * store.subscribe((state, changes) => {
 *   console.log('State changed:', changes);
 * });
 */
export function createStore<TConfig extends ConfigSchema>(
  config: ValidatedConfig<TConfig>,
  options?: StoreOptions
): Store<TConfig> {
  return new Store(config, options);
}

